import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Search, FileCheck, TrendingUp } from "lucide-react";


export const ATSExplainer = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-muted/30 to-muted/10 py-20">
      <div className="container mx-auto px-4">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-bold md:text-5xl">
            Your Resume Is Probably Invisible (Here's Why)
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground md:text-xl">
            75% of resumes never make it past applicant tracking systems. Our AI rewrites yours for visibility.
          </p>
        </div>

        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 md:grid-cols-3">
            {/* Card 1: Keyword Matching */}
            <Card className="group overflow-hidden p-6 transition-all hover:shadow-lg">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 transition-all group-hover:scale-110">
                <Search className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mb-3 text-xl font-semibold">Keyword Matching</h3>
              <p className="mb-4 text-muted-foreground">
                ATS systems scan for specific keywords from the job description. Our AI identifies and naturally integrates the exact terms recruiters are searching for.
              </p>
              <div className="mt-4 rounded-lg bg-primary/5 p-3">
                <p className="text-sm font-medium text-primary">
                  ✓ Analyzes job postings
                  <br />
                  ✓ Adds relevant keywords
                  <br />
                  ✓ Natural integration
                </p>
              </div>
            </Card>

            {/* Card 2: Formatting Compliance */}
            <Card className="group overflow-hidden p-6 transition-all hover:shadow-lg">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 transition-all group-hover:scale-110">
                <FileCheck className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mb-3 text-xl font-semibold">Formatting Compliance</h3>
              <p className="mb-4 text-muted-foreground">
                If your resume isn't formatted properly, it's invisible to 90% of hiring teams. We use clean, ATS-friendly layouts that every system can read.
              </p>
              <div className="mt-4 rounded-lg bg-primary/5 p-3">
                <p className="text-sm font-medium text-primary">
                  ✓ No complex tables
                  <br />
                  ✓ Standard sections
                  <br />
                  ✓ Clean structure
                </p>
              </div>
            </Card>

            {/* Card 3: AI Scoring Advantage */}
            <Card className="group overflow-hidden p-6 transition-all hover:shadow-lg">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 transition-all group-hover:scale-110">
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
              <h3 className="mb-3 text-xl font-semibold">AI Scoring Advantage</h3>
              <p className="mb-4 text-muted-foreground">
                Our AI optimizes your resume to score higher in ATS algorithms by emphasizing measurable achievements and using data-backed formatting strategies.
              </p>
              <div className="mt-4 rounded-lg bg-primary/5 p-3">
                <p className="text-sm font-medium text-primary">
                  ✓ Quantified results
                  <br />
                  ✓ Action verbs
                  <br />
                  ✓ Optimized structure
                </p>
              </div>
            </Card>
          </div>

          {/* CTA Card */}
          <div className="mt-12">
            <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 p-8">
              <div className="text-center">
                <h3 className="mb-4 text-2xl font-bold">
                  Stop guessing. Start getting interviews.
                </h3>
                <p className="mb-6 text-muted-foreground">
                  Our AI doesn't just format your resume—it rewrites it to pass ATS filters and catch recruiter attention. The difference? You actually get callbacks.
                </p>
                <Link to="/ats-optimization">
                  <Button size="lg" className="gap-2">
                    Learn More About ATS Optimization
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
